import { useMediaQuery } from '@material-ui/core'
import { LAMPORTS_PER_SOL } from '@solana/web3.js'
import { useAtom } from 'jotai'
import moment from 'moment'
import { useRouter } from 'next/router'
import * as React from 'react'
import { animated, useSpring } from 'react-spring'
import styled from 'styled-components'
import { EnrichedReceipt } from '.'
import { rwSolPriceAtom } from '../../../../atoms/WalletName'
import ProfileImage from '../../../profile/image'
import SkeletonItem from './SkeletonItem'

const OuterDiv = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  margin-top: 15px;
`

const InnerDiv = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`

const EventDiv = styled.div`
  display: flex;
  flex-direction: column;

  @media (min-width: 43em) {
    margin-left: 15px;
  }
`

const EndDiv = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`

const PriceDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`

const TitleDiv = styled.div`
  display: flex;
  flex-direction: row;
`

const Title = styled(animated.span)<{ neutral: boolean }>`
  color: ${(props) => (props.neutral ? 'white' : 'rgb(48, 209, 88)')};
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 5px;
  white-space: pre;

  @media (min-width: 43em) {
    font-size: 17px;
  }
`

const Subtitle = styled.div`
  color: #cdcdcd;
  font-size: 14px;
  font-weight: 400;

  @media (min-width: 43em) {
    font-size: 17px;
  }
`

const StartDiv = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`

const Divider = styled.div`
  width: 100%;
  background: #323232;
  height: 1px;
  margin-top: 15px;
`

// const ProfileImage = styled.img`
//   object-fit: contain;
//   height: 40px;
//   width: 40px;
//   border-radius: 20px;
//   cursor: pointer;

//   @media (min-width: 43em) {
//     height: 50px;
//     width: 50px;
//     border-radius: 25px;
//   }
// `
interface ActivityItemProps {
  receipt?: EnrichedReceipt
}

const ActivityItem = ({ receipt }: ActivityItemProps): JSX.Element => {
  const [solPrice] = useAtom(rwSolPriceAtom)
  const isMobile = useMediaQuery('(max-width: 43em)')
  const [profileHover, setProfileHover] = React.useState(false)
  const [secondProfileHover, setSecondProfileHover] = React.useState(false)
  const { buyer, seller, receiptType } = receipt || {}
  const createdAt = receipt?.createdAt ? new Date(receipt.createdAt) : new Date()
  const price = receipt?.price
    ? String(Math.round((100 * receipt.price) / LAMPORTS_PER_SOL) / 100)
    : '0'
  const router = useRouter()

  const profileHoverState = useSpring({
    cursor: 'pointer',
    borderRadius: isMobile ? 40 : 50,
    transform: profileHover ? `translate3d(0,-5%,0)` : `translate3d(0,0%,0)`,
    config: {
      mass: 1,
      tension: 170,
      friction: 26,
      velocity: 0,
    },
  })

  const firstHoverState = useSpring({
    cursor: 'pointer',
    textDecoration: profileHover ? 'underline' : 'none',
    config: {
      mass: 1,
      tension: 170,
      friction: 26,
      velocity: 0,
    },
  })

  const secondHoverState = useSpring({
    cursor: 'pointer',
    textDecoration: secondProfileHover ? 'underline' : 'none',
    config: {
      mass: 1,
      tension: 170,
      friction: 26,
      velocity: 0,
    },
  })

  const openProfile = (address: string): void => {
    router.push(`/${address}`)
  }

  if (!receipt) {
    return <SkeletonItem />
  }

  return (
    <OuterDiv>
      <InnerDiv>
        <StartDiv>
          {!isMobile && buyer?.address && (
            <animated.div
              onMouseEnter={() => setProfileHover(true)}
              onMouseLeave={() => setProfileHover(false)}
              style={profileHoverState}
              onClick={() => openProfile(buyer.address)}
            >
              <ProfileImage item={buyer} size={isMobile ? 40 : 50} />
            </animated.div>
          )}
          <EventDiv>
            <TitleDiv>
              {buyer?.displayName && (
                <Title
                  neutral={true}
                  onMouseEnter={() => setProfileHover(true)}
                  onMouseLeave={() => setProfileHover(false)}
                  style={firstHoverState}
                  onClick={() => openProfile(buyer.address)}
                >
                  {buyer.displayName}
                </Title>
              )}
              <Title neutral>
                {(receiptType === 'primary' ? ' minted' : ' bought') +
                  (isMobile
                    ? ''
                    : ` ${
                        receipt.tokenSize > 1 && receipt.tokenSize ? receipt.tokenSize : 'an'
                      } edition${receipt.tokenSize > 1 && receipt.tokenSize ? 's' : ''}`)}
              </Title>
              {seller?.displayName && <Title neutral>{` from `}</Title>}
              {seller?.displayName && (
                <Title
                  neutral
                  onMouseEnter={() => setSecondProfileHover(true)}
                  onMouseLeave={() => setSecondProfileHover(false)}
                  style={secondHoverState}
                  onClick={() => openProfile(seller.address)}
                >
                  {seller.displayName}
                </Title>
              )}
              <Title neutral>.</Title>
            </TitleDiv>
            <Subtitle>
              {moment(createdAt) > moment(new Date()).add(-1, 'day') || isMobile
                ? moment(createdAt).fromNow()
                : moment(createdAt).format('dddd, MMMM D, YYYY') +
                  ' at ' +
                  moment(createdAt).format('h:mm a')}
            </Subtitle>
          </EventDiv>
        </StartDiv>
        <EndDiv>
          <PriceDiv>
            <Title neutral={false}>{price + ' SOL'}</Title>
            <Subtitle>${(Number(price) * solPrice).toFixed(2)}</Subtitle>
          </PriceDiv>
        </EndDiv>
      </InnerDiv>
      <Divider />
    </OuterDiv>
  )
}

export default ActivityItem
